import { FileText } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'

export interface InvoiceData {
  rfc: string
  name: string
  cfdi_use: string
  fiscal_regime: string
  zip_code: string
  email?: string
}

export interface InvoiceDataModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: InvoiceData
  setData: (data: InvoiceData) => void
  onConfirm: () => void
}

export function InvoiceDataModal({
  open,
  onOpenChange,
  data,
  setData,
  onConfirm,
}: InvoiceDataModalProps) {
  const isComplete = data.rfc && data.name && data.cfdi_use && data.fiscal_regime && data.zip_code
  const isPublicoGeneral = data.rfc === 'XAXX010101000'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Datos de Facturación
          </DialogTitle>
          <DialogDescription>
            {isPublicoGeneral
              ? 'Datos predeterminados para facturación a Público en General.'
              : 'Ingrese los datos fiscales del cliente para generar la factura.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">RFC</label>
              {isPublicoGeneral ? (
                <div className="rounded-md border border-border bg-card p-2 text-sm font-medium text-card-foreground">{data.rfc}</div>
              ) : (
                <Input
                  value={data.rfc}
                  onChange={(e) => setData({ ...data, rfc: e.target.value.toUpperCase() })}
                  placeholder="XAXX010101000"
                  maxLength={13}
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">Código Postal</label>
              {isPublicoGeneral ? (
                <div className="h-10 rounded-md border border-border bg-card px-3 text-sm font-medium text-card-foreground flex items-center">{data.zip_code}</div>
              ) : (
                <Input
                  value={data.zip_code}
                  onChange={(e) => setData({ ...data, zip_code: e.target.value })}
                  placeholder="Ej. 06600"
                  maxLength={5}
                />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">Razón Social</label>
            {isPublicoGeneral ? (
              <div className="rounded-md border border-border bg-card p-2 text-sm font-medium text-card-foreground">{data.name}</div>
            ) : (
              <Input
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value.toUpperCase() })}
                placeholder="PUBLICO EN GENERAL"
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">Régimen Fiscal</label>
              {isPublicoGeneral ? (
                <div className="rounded-md border border-border bg-card p-2 text-sm font-medium text-card-foreground">Sin obligaciones fiscales (616)</div>
              ) : (
                <Select value={data.fiscal_regime} onValueChange={(val) => setData({ ...data, fiscal_regime: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="616">Sin obligaciones fiscales (616)</SelectItem>
                    <SelectItem value="601">General de Ley Personas Morales (601)</SelectItem>
                    <SelectItem value="605">Sueldos y Salarios (605)</SelectItem>
                    <SelectItem value="606">Arrendamiento (606)</SelectItem>
                    <SelectItem value="612">Personas Físicas con Actividades Emp (612)</SelectItem>
                    <SelectItem value="621">Incorporación Fiscal (621)</SelectItem>
                    <SelectItem value="626">RESICO (626)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">Uso de CFDI</label>
              {isPublicoGeneral ? (
                <div className="rounded-md border border-border bg-card p-2 text-sm font-medium text-card-foreground">Sin efectos fiscales (S01)</div>
              ) : (
                <Select value={data.cfdi_use} onValueChange={(val) => setData({ ...data, cfdi_use: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="S01">Sin efectos fiscales (S01)</SelectItem>
                    <SelectItem value="G01">Adquisición de mercancías (G01)</SelectItem>
                    <SelectItem value="G03">Gastos en general (G03)</SelectItem>
                    <SelectItem value="D01">Honorarios médicos, dentales y gastos hospitalarios (D01)</SelectItem>
                    <SelectItem value="CP01">Pagos (CP01)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-2 rounded-lg border border-primary/20 bg-card p-3">
            <label className="text-sm font-medium text-card-foreground">Correo Electrónico (Opcional)</label>
            <Input
              value={data.email || ''}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              placeholder="cliente@ejemplo.com"
              type="email"
              className="border-border bg-card text-card-foreground focus-visible:ring-primary"
            />
            <p className="text-xs text-card-description">
              Se enviará automáticamente el PDF y XML de la factura a esta dirección.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Volver
          </Button>
          <Button onClick={onConfirm} disabled={!isComplete}>
            Confirmar Datos
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
